const CACHE_PREFIX = 'delper_cache_';
const CACHE_VERSION_KEY = 'delper_cache_version';

const DEFAULT_TTL = 60 * 60 * 1000; // 1 hour in milliseconds

export const CacheKeys = {
  CATEGORIES: 'categories',
  SUBCATEGORIES: 'subcategories',
  BACKGROUNDS: 'backgrounds',
  FONTS: 'fonts',
  USER_PROFILE: 'user_profile',
  USER_FAVORITES: 'user_favorites',
  PRICING_PLANS: 'pricing_plans',
  CACHE_VERSION: 'cache_version'
};

function getFullKey(key) {
  return CACHE_PREFIX + key;
}

export function getCacheVersion() {
  try {
    return localStorage.getItem(CACHE_VERSION_KEY) || '1';
  } catch (e) {
    return '1';
  }
}

export function setCacheVersion(version) {
  try {
    localStorage.setItem(CACHE_VERSION_KEY, version);
  } catch (e) {
    console.warn('Failed to set cache version:', e);
  }
}

export function setCache(key, data, ttl = DEFAULT_TTL) {
  try {
    const cacheItem = {
      data,
      timestamp: Date.now(),
      ttl,
      version: getCacheVersion()
    };
    localStorage.setItem(getFullKey(key), JSON.stringify(cacheItem));
    return true;
  } catch (e) {
    console.warn('Failed to set cache:', e);
    return false;
  }
}

export function getCache(key) {
  try {
    const item = localStorage.getItem(getFullKey(key));
    if (!item) return null;

    const cacheItem = JSON.parse(item);
    const now = Date.now();
    const currentVersion = getCacheVersion();

    if (cacheItem.version !== currentVersion) {
      removeCache(key);
      return null;
    }

    if (now - cacheItem.timestamp > cacheItem.ttl) {
      removeCache(key);
      return null;
    }

    return cacheItem.data;
  } catch (e) {
    console.warn('Failed to get cache:', e);
    return null;
  }
}

export function removeCache(key) {
  try {
    localStorage.removeItem(getFullKey(key));
    return true;
  } catch (e) {
    console.warn('Failed to remove cache:', e);
    return false;
  }
}

// Category data is cached per locale so an admin change must invalidate every
// localized copy, not only the locale currently selected in the UI.
export function invalidateCategoryCaches() {
  ['ru', 'en'].forEach((locale) => {
    removeCache(`${CacheKeys.CATEGORIES}_${locale}`);
  });
  removeCache(CacheKeys.CATEGORIES);
  return true;
}

export function clearAllCache() {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    return true;
  } catch (e) {
    console.warn('Failed to clear cache:', e);
    return false;
  }
}

export function invalidateCache() {
  const newVersion = Date.now().toString();
  setCacheVersion(newVersion);
  clearAllCache();
  return newVersion;
}

export async function clearServiceWorkerCache() {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data.success);
      };
      navigator.serviceWorker.controller.postMessage(
        { type: 'CLEAR_ALL_CACHES' },
        [messageChannel.port2]
      );
      setTimeout(() => resolve(false), 5000);
    });
  }
  return Promise.resolve(false);
}

export async function clearAllCachesComplete() {
  clearAllCache();
  const swCleared = await clearServiceWorkerCache();
  return { localStorage: true, serviceWorker: swCleared };
}

export function isCacheValid(key) {
  return getCache(key) !== null;
}

export default {
  CacheKeys,
  setCache,
  getCache,
  removeCache,
  invalidateCategoryCaches,
  clearAllCache,
  invalidateCache,
  getCacheVersion,
  setCacheVersion,
  isCacheValid,
  clearServiceWorkerCache,
  clearAllCachesComplete
};
